import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter, distinctUntilChanged, Subject, takeUntil } from 'rxjs';

export interface Breadcrumb {
  label: string;
  url: string;
  icon?: string;
}

// Route label mappings for readable breadcrumbs
const ROUTE_LABELS: { [key: string]: { label: string; icon?: string } } = {
  // Main sections
  'dashboard': { label: 'Dashboard', icon: 'dashboard' },
  'master': { label: 'Masters', icon: 'storage' },
  'accounting': { label: 'Accounting', icon: 'account_balance' },
  'reports': { label: 'Reports', icon: 'insert_chart' },

  // Masters - Product
  'product': { label: 'Products', icon: 'inventory_2' },
  'process': { label: 'Process', icon: 'settings' },
  'category': { label: 'Product Categories', icon: 'category' },
  'jobchecklist': { label: 'Job Checklist', icon: 'checklist' },
  'shipment': { label: 'Shipment Types', icon: 'local_shipping' },
  'hsn': { label: 'HSN / SAC', icon: 'tag' },
  'pricelist': { label: 'Price List', icon: 'sell' },
  'shade': { label: 'Shade', icon: 'palette' },

  // Masters - Doctor
  'doctor': { label: 'Doctors', icon: 'medical_services' },
  'state': { label: 'States', icon: 'map' },
  'area': { label: 'Cities', icon: 'location_city' },

  // Job Entry
  'joborder': { label: 'Job Orders', icon: 'format_list_bulleted' },
  'remark': { label: 'Remarks', icon: 'comment' },
  'order': { label: 'Orders', icon: 'receipt' },

  // Production
  'jobschedule': { label: 'Job Schedule', icon: 'schedule' },
  'prodjobin': { label: 'Production Job In', icon: 'login' },
  'prodjobout': { label: 'Production Job Out', icon: 'logout' },
  'employeelogin': { label: 'Employee Login', icon: 'badge' },

  // Company Setup
  'company': { label: 'Company', icon: 'business' },
  'employee': { label: 'Employees', icon: 'people' },
  'department': { label: 'Departments', icon: 'domain' },

  // Challan
  'challan': { label: 'Challan', icon: 'local_shipping' },

  // Accounting / Invoicing
  'proforma': { label: 'Proforma Invoice', icon: 'receipt_long' },
  'invoice': { label: 'Invoice', icon: 'receipt' },
  'account': { label: 'Accounts', icon: 'account_balance_wallet' },
  'account_receipt': { label: 'Account Receipt', icon: 'payments' },
  'note': { label: 'Credit/Debit Note', icon: 'note' },
  'journal_voucher': { label: 'Journal Voucher', icon: 'book' },
  'knock_off': { label: 'Knock Off', icon: 'link_off' },
  'balance': { label: 'Balance', icon: 'account_balance' },
  'sales_return': { label: 'Sales Return', icon: 'assignment_return' },
  'production-transfer': { label: 'Production Transfer', icon: 'swap_horiz' },

  // Access Control
  'access-control': { label: 'Access Control', icon: 'security' },
  'userrole': { label: 'User Roles', icon: 'admin_panel_settings' },
  'auth': { label: 'Users', icon: 'group' },

  // Reports sub-pages
  'pendingJobData': { label: 'Pending Job Data', icon: 'pending_actions' },
  'accountAging': { label: 'Account Aging', icon: 'hourglass_bottom' },
  'jobOrderLog': { label: 'Job Order Log', icon: 'history' },
  'allDoctors': { label: 'All Doctors', icon: 'medical_services' },
  'productionJobData': { label: 'Production Details', icon: 'precision_manufacturing' },
  'dailyJobActivity': { label: 'Daily Job Activity', icon: 'today' },
  'taxInvoice': { label: 'Tax Invoice', icon: 'description' },
  'account_ledger': { label: 'Account Ledger', icon: 'menu_book' },

  // Misc
  'change-password': { label: 'Change Password', icon: 'lock' },

  // Common actions
  'create': { label: 'Create', icon: 'add' },
  'edit': { label: 'Edit', icon: 'edit' },
};

// Sections grouping for context-aware breadcrumbs
const SECTION_MAP: { [key: string]: string } = {
  'product': 'master',
  'process': 'master',
  'category': 'master',
  'jobchecklist': 'master',
  'shipment': 'master',
  'hsn': 'master',
  'pricelist': 'master',
  'shade': 'master',
  'doctor': 'master',
  'state': 'master',
  'area': 'master',
  'remark': 'master',
  'proforma': 'accounting',
  'invoice': 'accounting',
  'account': 'accounting',
  'account_receipt': 'accounting',
  'note': 'accounting',
  'journal_voucher': 'accounting',
  'knock_off': 'accounting',
  'balance': 'accounting',
  'sales_return': 'accounting',
  'company': 'company',
  'employee': 'company',
  'department': 'company',
  'userrole': 'access',
  'auth': 'access',
  'access-control': 'access',
};

const SECTION_LABELS: { [key: string]: { label: string; url: string; icon: string } } = {
  'master': { label: 'Masters', url: '/master', icon: 'storage' },
  'accounting': { label: 'Accounting', url: '/accounting', icon: 'account_balance' },
  'company': { label: 'Company Setup', url: '/company', icon: 'business' },
  'access': { label: 'Access Control', url: '/access-control', icon: 'security' },
};

@Component({
  selector: 'app-breadcrumb',
  templateUrl: './breadcrumb.component.html',
  styleUrls: ['./breadcrumb.component.css']
})
export class BreadcrumbComponent implements OnInit, OnDestroy {
  breadcrumbs: Breadcrumb[] = [];
  private destroy$ = new Subject<void>();

  constructor(private router: Router) {}

  ngOnInit(): void {
    this.buildBreadcrumbs();
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.buildBreadcrumbs();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private buildBreadcrumbs(): void {
    const url = this.router.url.split('?')[0];
    const segments = url.split('/').filter(segment => segment);
    this.breadcrumbs = [];

    if (segments.length === 0) return;

    const firstSegment = segments[0];

    // Skip breadcrumb for dashboard itself
    if (firstSegment === 'dashboard') return;

    // Add section breadcrumb if entity belongs to a section
    const section = SECTION_MAP[firstSegment];
    if (section) {
      const sectionInfo = SECTION_LABELS[section];
      this.breadcrumbs.push({
        label: sectionInfo.label,
        url: sectionInfo.url,
        icon: sectionInfo.icon
      });
    }

    // Build breadcrumbs from URL segments
    let currentUrl = '';
    for (const segment of segments) {
      // Skip numeric IDs — replace with "Edit" breadcrumb
      if (/^\d+$/.test(segment)) {
        const prevCrumb = this.breadcrumbs[this.breadcrumbs.length - 1];
        if (prevCrumb) {
          // Add "Edit" or "Create" breadcrumb
          currentUrl += `/${segment}`;
          if (segment === '0') {
            this.breadcrumbs.push({ label: 'Create', url: currentUrl, icon: 'add' });
          } else {
            this.breadcrumbs.push({ label: 'Edit', url: currentUrl, icon: 'edit' });
          }
        }
        continue;
      }

      // Skip employeelogin type segments (in/out)
      if (segment === 'in' || segment === 'out') {
        const prevCrumb = this.breadcrumbs[this.breadcrumbs.length - 1];
        if (prevCrumb && prevCrumb.label === 'Employee Login') {
          prevCrumb.label = segment === 'in' ? 'Job In Details' : 'Job Out Details';
          currentUrl += `/${segment}`;
          prevCrumb.url = currentUrl;
        }
        continue;
      }

      currentUrl += `/${segment}`;

      // Skip if this segment is the same as the section we already added
      if (section && SECTION_LABELS[section]?.url === currentUrl) continue;

      const routeInfo = ROUTE_LABELS[segment];
      this.breadcrumbs.push({
        label: routeInfo?.label || this.formatLabel(segment),
        url: currentUrl,
        icon: routeInfo?.icon
      });
    }
  }

  private formatLabel(segment: string): string {
    return segment
      .split(/[-_]/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }
}
